import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle,
  ChevronRight,
  FlaskConical,
  Percent,
  Plus,
  Trash2,
  Users,
} from "lucide-react";

/**
 * Deployment pipelines: the ordered stages an app version passes through.
 *
 * The backend (services/deployment_pipeline.py) is the authority; the rules
 * below mirror its normalise_stages() so the user sees a problem as they
 * type instead of as a 400 on save. If the two ever disagree, the server
 * wins and its `detail` message is shown in a toast by the calling page.
 *
 *   pre_uat     optional, at most one, before UAT. Existing Entra group,
 *               published as Available, no testers, moved to UAT by hand.
 *   uat         exactly one. The test group, testers and approvals.
 *   group       after UAT. An existing Entra group, assigned Required.
 *               ALICE never creates it. Cumulative: stays assigned as later
 *               stages follow.
 *   percentage  after UAT. A random sample of the family's devices;
 *               percentages strictly increase, 1-100.
 *
 * Nothing in here hard-codes the percentages of the built-in Standard
 * template: the server sends that template like any other.
 */

export const STAGE_PRE_UAT = "pre_uat";
export const STAGE_UAT = "uat";
export const STAGE_GROUP = "group";
export const STAGE_PERCENTAGE = "percentage";

// Mirrors MAX_PHASES / MAX_NAME_LENGTH / MAX_GROUP_NAME_LENGTH on the server.
export const MAX_STAGES_AFTER_UAT = 10;
const MAX_NAME_LENGTH = 60;
const MAX_GROUP_NAME_LENGTH = 256;

// Names ALICE generated for percentage stages ("Phase 2"). They are
// renumbered when stages are added, removed or moved, so the numbering keeps
// counting percentage stages only, as the server does. A name the user typed
// themselves is left alone.
const DEFAULT_PHASE_NAME_RE = /^Phase \d+$/;

const TYPE_LABELS = {
  [STAGE_PRE_UAT]: { label: "Pre-UAT", detail: "Available, moved on by hand", Icon: FlaskConical },
  [STAGE_UAT]: { label: "UAT", detail: "Test group and tester approvals", Icon: CheckCircle },
  [STAGE_GROUP]: { label: "Named group", detail: "Existing Entra group, Required", Icon: Users },
  [STAGE_PERCENTAGE]: { label: "Percentage", detail: "Random sample of the app's devices, Required", Icon: Percent },
};

/** A stage id the server accepts as-is (^[a-z0-9_-]{1,40}$). */
export const newStageId = () => `s${Math.random().toString(16).slice(2, 10)}`;

export const isPostUatStage = (stage) =>
  stage?.type === STAGE_GROUP || stage?.type === STAGE_PERCENTAGE;

/** The name the server gives a stage whose name is left blank. */
export function defaultStageName(stage, index, stages) {
  switch (stage?.type) {
    case STAGE_PRE_UAT:
      return "Pre-UAT";
    case STAGE_UAT:
      return "UAT";
    case STAGE_GROUP:
      return (stage.group_name || "").trim() || "Named group";
    case STAGE_PERCENTAGE: {
      const k = (stages || [])
        .slice(0, index + 1)
        .filter((s) => s?.type === STAGE_PERCENTAGE).length;
      return `Phase ${k || 1}`;
    }
    default:
      return "Stage";
  }
}

export function stageDisplayName(stage, index, stages) {
  return (stage?.name || "").trim() || defaultStageName(stage, index, stages);
}

const parseWhole = (value) => {
  if (value === null || value === undefined) return { empty: true };
  const text = String(value).trim();
  if (text === "") return { empty: true };
  if (!/^\d+$/.test(text)) return { invalid: true };
  return { number: parseInt(text, 10) };
};

/**
 * Validate a stage list the way the server will.
 * Returns { valid, general: string[], byStage: { [index]: string[] } }.
 */
export function validatePipelineStages(stages) {
  const general = [];
  const byStage = {};
  const add = (index, message) => {
    byStage[index] = byStage[index] || [];
    byStage[index].push(message);
  };

  if (!Array.isArray(stages) || stages.length === 0) {
    return {
      valid: false,
      general: ["A pipeline needs a UAT stage and at least one stage after it."],
      byStage,
    };
  }

  let uatIndex = null;
  let preUatSeen = false;
  let lastPercentage = 0;
  let stagesAfterUat = 0;
  const seenGroups = new Map();

  stages.forEach((stage, index) => {
    const type = stage?.type;
    if ((stage?.name || "").trim().length > MAX_NAME_LENGTH) {
      add(index, `Names can be at most ${MAX_NAME_LENGTH} characters.`);
    }

    if (type === STAGE_UAT) {
      if (uatIndex !== null) add(index, "A pipeline can have only one UAT stage.");
      else uatIndex = index;
    } else if (type === STAGE_PRE_UAT) {
      if (uatIndex !== null) add(index, "Pre-UAT must come before UAT.");
      if (preUatSeen) add(index, "A pipeline can have only one Pre-UAT stage.");
      preUatSeen = true;
    } else if (isPostUatStage(stage)) {
      if (uatIndex === null) add(index, "This stage must come after UAT.");
      stagesAfterUat += 1;
    } else {
      add(index, `Unknown stage type '${type}'.`);
    }

    if (type === STAGE_PRE_UAT || type === STAGE_GROUP) {
      const groupName = (stage.group_name || "").trim();
      if (!groupName) {
        add(index, "Enter the name of an existing Entra group.");
      } else if (groupName.length > MAX_GROUP_NAME_LENGTH) {
        add(index, `Group names can be at most ${MAX_GROUP_NAME_LENGTH} characters.`);
      } else {
        const key = groupName.toLowerCase();
        if (seenGroups.has(key)) {
          add(index, `The group '${groupName}' is already used by another stage.`);
        } else {
          seenGroups.set(key, index);
        }
      }
    }

    if (type === STAGE_PERCENTAGE) {
      const pct = parseWhole(stage.percentage);
      if (pct.empty) {
        add(index, "Enter a percentage.");
      } else if (pct.invalid || pct.number < 1 || pct.number > 100) {
        add(index, "Percentage must be a whole number from 1 to 100.");
      } else {
        if (pct.number <= lastPercentage) {
          add(index, `Must be more than the previous percentage stage (${lastPercentage}%).`);
        }
        lastPercentage = Math.max(lastPercentage, pct.number);
      }
    }

    if (isPostUatStage(stage)) {
      const threshold = parseWhole(stage.threshold);
      if (!threshold.empty && (threshold.invalid || threshold.number > 100)) {
        add(index, "Threshold must be a whole number from 0 to 100, or blank for the default.");
      }
    }
  });

  if (uatIndex === null) general.push("A pipeline needs a UAT stage.");
  if (stagesAfterUat === 0) general.push("Add at least one stage after UAT.");
  if (stagesAfterUat > MAX_STAGES_AFTER_UAT) {
    general.push(`A pipeline can have at most ${MAX_STAGES_AFTER_UAT} stages after UAT.`);
  }

  const valid = general.length === 0 && Object.keys(byStage).length === 0;
  return { valid, general, byStage };
}

/** The first validation message, for a toast when saving is refused. */
export function firstPipelineError(stages) {
  const result = validatePipelineStages(stages);
  if (result.valid) return null;
  if (result.general.length) return result.general[0];
  const index = Number(Object.keys(result.byStage)[0]);
  const name = stageDisplayName(stages[index], index, stages);
  return `${name}: ${result.byStage[index][0]}`;
}

/**
 * The stage list as the API wants it: trimmed strings, whole numbers, no
 * blank thresholds. group_id is kept only where the editor left it in place
 * (it is dropped as soon as the group name is edited, so the server
 * re-resolves the group in Entra).
 */
export function cleanStagesForSave(stages) {
  return (stages || []).map((stage) => {
    const out = { type: stage.type };
    if (stage.id) out.id = stage.id;
    const name = (stage.name || "").trim();
    if (name) out.name = name;
    if (stage.type === STAGE_PRE_UAT || stage.type === STAGE_GROUP) {
      out.group_name = (stage.group_name || "").trim();
      if (stage.group_id) out.group_id = stage.group_id;
    }
    if (stage.type === STAGE_PERCENTAGE) {
      const pct = parseWhole(stage.percentage);
      out.percentage = pct.number ?? stage.percentage;
    }
    if (isPostUatStage(stage)) {
      const threshold = parseWhole(stage.threshold);
      if (!threshold.empty) out.threshold = threshold.number ?? stage.threshold;
    }
    return out;
  });
}

/** A deep enough copy to edit without touching the source (e.g. a template). */
export const copyStages = (stages) => (stages || []).map((s) => ({ ...s }));

const renumberPhaseNames = (stages) => {
  let k = 0;
  return stages.map((stage) => {
    if (stage.type !== STAGE_PERCENTAGE) return stage;
    k += 1;
    const name = (stage.name || "").trim();
    if (!name || DEFAULT_PHASE_NAME_RE.test(name)) {
      return { ...stage, name: `Phase ${k}` };
    }
    return stage;
  });
};

const percentLabel = (stage) => {
  const pct = parseWhole(stage?.percentage);
  return pct.number !== undefined ? `${pct.number}%` : null;
};

/**
 * Compact read-only chain: "Pre-UAT → UAT → Pilot → Phase 1 10% → ...".
 * Every label comes from the stages passed in.
 */
export function PipelineStagesSummary({ stages, className = "", testId = "pipeline-summary" }) {
  const list = Array.isArray(stages) ? stages : [];
  if (list.length === 0) {
    return (
      <span className={`text-xs text-white/60 ${className}`} data-testid={testId}>
        No stages
      </span>
    );
  }
  return (
    <div className={`flex flex-wrap items-center gap-1 text-xs ${className}`} data-testid={testId}>
      {list.map((stage, index) => {
        const name = stageDisplayName(stage, index, list);
        const pct = stage.type === STAGE_PERCENTAGE ? percentLabel(stage) : null;
        const isGroup = stage.type === STAGE_GROUP || stage.type === STAGE_PRE_UAT;
        const groupName = (stage.group_name || "").trim();
        const title = isGroup && groupName && groupName !== name ? `Entra group: ${groupName}` : undefined;
        return (
          <React.Fragment key={stage.id || index}>
            {index > 0 && <ChevronRight className="h-3 w-3 text-white/50 shrink-0" aria-hidden="true" />}
            <span
              className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/10 px-2 py-0.5 text-white/90 whitespace-nowrap"
              title={title}
              data-testid={`${testId}-stage-${index}`}
            >
              {isGroup && <Users className="h-3 w-3 text-white/60" aria-hidden="true" />}
              {name}
              {pct && <span className="font-semibold text-white">{pct}</span>}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * Edit an ordered stage list. Controlled: `stages` in, `onChange(next)` out.
 *
 * Props:
 *   stages, onChange, readOnly
 *   defaultThresholds  optional { first, later } from the tenant's
 *                      automation settings, shown as the placeholder for a
 *                      stage that leaves its threshold blank.
 *   testIdPrefix       data-testid prefix (default "pipeline-editor").
 */
export default function PipelineStagesEditor({
  stages,
  onChange,
  readOnly = false,
  defaultThresholds = null,
  testIdPrefix = "pipeline-editor",
}) {
  const list = Array.isArray(stages) ? stages : [];
  const validation = validatePipelineStages(list);
  const uatIndex = list.findIndex((s) => s?.type === STAGE_UAT);
  const hasPreUat = list.some((s) => s?.type === STAGE_PRE_UAT);
  const postUatIndexes = list.map((s, i) => (isPostUatStage(s) ? i : -1)).filter((i) => i >= 0);
  const lastPostUatIndex = postUatIndexes.length ? postUatIndexes[postUatIndexes.length - 1] : -1;
  const atMax = postUatIndexes.length >= MAX_STAGES_AFTER_UAT;

  const emit = (next) => onChange && onChange(next);

  const updateStage = (index, patch) => {
    const next = list.map((stage, i) => {
      if (i !== index) return stage;
      const updated = { ...stage, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "group_name")) {
        // The stored group_id belongs to the old name. Dropping it makes the
        // server look the new name up in Entra on save (and refuse it with a
        // 400 if no such group exists).
        delete updated.group_id;
        if (stage.type === STAGE_GROUP) {
          const oldName = (stage.name || "").trim();
          const oldGroup = (stage.group_name || "").trim();
          // Keep a name that simply followed the group name in step.
          if (!oldName || oldName === oldGroup) updated.name = patch.group_name;
        }
      }
      return updated;
    });
    emit(next);
  };

  const addPreUat = () => {
    if (hasPreUat) return;
    const stage = {
      id: list.some((s) => s.id === STAGE_PRE_UAT) ? newStageId() : STAGE_PRE_UAT,
      type: STAGE_PRE_UAT,
      name: "Pre-UAT",
      group_name: "",
    };
    const at = uatIndex >= 0 ? uatIndex : 0;
    emit([...list.slice(0, at), stage, ...list.slice(at)]);
  };

  const addGroup = () => {
    if (atMax) return;
    // Named rings (Pilot, Friends and Family) usually come before the
    // percentage samples, so a new one goes in ahead of them.
    const firstPct = list.findIndex((s) => s?.type === STAGE_PERCENTAGE);
    const at = firstPct >= 0 ? firstPct : list.length;
    const stage = { id: newStageId(), type: STAGE_GROUP, name: "", group_name: "" };
    emit(renumberPhaseNames([...list.slice(0, at), stage, ...list.slice(at)]));
  };

  const addPercentage = () => {
    if (atMax) return;
    const stage = { id: newStageId(), type: STAGE_PERCENTAGE, name: "", percentage: "" };
    emit(renumberPhaseNames([...list, stage]));
  };

  const removeStage = (index) => {
    if (list[index]?.type === STAGE_UAT) return;
    emit(renumberPhaseNames(list.filter((_, i) => i !== index)));
  };

  const moveStage = (index, direction) => {
    const target = index + direction;
    if (!isPostUatStage(list[index]) || !isPostUatStage(list[target])) return;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    emit(renumberPhaseNames(next));
  };

  const thresholdPlaceholder = (index) => {
    if (!defaultThresholds) return "Default";
    const value = index === postUatIndexes[0] ? defaultThresholds.first : defaultThresholds.later;
    return value === undefined || value === null ? "Default" : `Default (${value}%)`;
  };

  if (readOnly) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-readonly`}>
        {list.map((stage, index) => {
          const meta = TYPE_LABELS[stage.type] || { label: stage.type, detail: "", Icon: ChevronRight };
          const Icon = meta.Icon;
          const groupName = (stage.group_name || "").trim();
          const threshold = parseWhole(stage.threshold);
          return (
            <div
              key={stage.id || index}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm"
              data-testid={`${testIdPrefix}-row-${index}`}
            >
              <span className="text-white/60 text-xs w-5">{index + 1}</span>
              <Icon className="h-4 w-4 text-white/70" aria-hidden="true" />
              <span className="font-medium text-white">{stageDisplayName(stage, index, list)}</span>
              <span className="text-xs text-white/60">{meta.label}</span>
              {groupName && <span className="text-xs text-white/80">Group: {groupName}</span>}
              {stage.type === STAGE_PERCENTAGE && percentLabel(stage) && (
                <span className="text-xs text-white/80">{percentLabel(stage)} of devices</span>
              )}
              {isPostUatStage(stage) && !threshold.empty && index !== lastPostUatIndex && (
                <span className="text-xs text-white/80">Threshold {threshold.number ?? stage.threshold}%</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      {list.map((stage, index) => {
        const meta = TYPE_LABELS[stage.type] || { label: stage.type, detail: "", Icon: ChevronRight };
        const Icon = meta.Icon;
        const errors = validation.byStage[index] || [];
        const postUat = isPostUatStage(stage);
        const canMoveUp = postUat && isPostUatStage(list[index - 1]);
        const canMoveDown = postUat && isPostUatStage(list[index + 1]);
        const isFinal = index === lastPostUatIndex;
        const groupName = (stage.group_name || "").trim();
        const rowId = `${testIdPrefix}-row-${index}`;
        return (
          <div
            key={stage.id || index}
            className={`rounded-lg border p-3 space-y-3 ${
              errors.length ? "border-red-400/60 bg-red-500/5" : "border-white/15 bg-white/5"
            }`}
            data-testid={rowId}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/20 text-xs text-white/80">
                  {index + 1}
                </span>
                <Icon className="h-4 w-4 text-white/70 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">{meta.label}</div>
                  <div className="text-xs text-white/60">{meta.detail}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {postUat && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-white/80 hover:text-white"
                      onClick={() => moveStage(index, -1)}
                      disabled={!canMoveUp}
                      aria-label="Move stage up"
                      data-testid={`${rowId}-up`}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-white/80 hover:text-white"
                      onClick={() => moveStage(index, 1)}
                      disabled={!canMoveDown}
                      aria-label="Move stage down"
                      data-testid={`${rowId}-down`}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {stage.type !== STAGE_UAT && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-white/80 hover:text-red-300"
                    onClick={() => removeStage(index)}
                    aria-label="Remove stage"
                    data-testid={`${rowId}-remove`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-white/80" htmlFor={`${rowId}-name`}>
                  Stage name
                </Label>
                <Input
                  id={`${rowId}-name`}
                  value={stage.name || ""}
                  maxLength={MAX_NAME_LENGTH}
                  onChange={(e) => updateStage(index, { name: e.target.value })}
                  placeholder={defaultStageName(stage, index, list)}
                  className="mt-1"
                  data-testid={`${rowId}-name`}
                />
              </div>

              {(stage.type === STAGE_PRE_UAT || stage.type === STAGE_GROUP) && (
                <div>
                  <Label className="text-xs text-white/80" htmlFor={`${rowId}-group`}>
                    Entra group name
                  </Label>
                  <Input
                    id={`${rowId}-group`}
                    value={stage.group_name || ""}
                    maxLength={MAX_GROUP_NAME_LENGTH}
                    onChange={(e) => updateStage(index, { group_name: e.target.value })}
                    placeholder="An existing group, e.g. Pilot users"
                    className="mt-1"
                    data-testid={`${rowId}-group`}
                  />
                  {stage.group_id && groupName ? (
                    <p className="mt-1 flex items-center gap-1 text-xs text-green-400" data-testid={`${rowId}-group-validated`}>
                      <CheckCircle className="h-3 w-3" />
                      Found in Entra ID
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-white/60">
                      Must already exist in Entra ID. ALICE checks it when you save and never creates it.
                    </p>
                  )}
                </div>
              )}

              {stage.type === STAGE_PERCENTAGE && (
                <div>
                  <Label className="text-xs text-white/80" htmlFor={`${rowId}-pct`}>
                    Devices (%)
                  </Label>
                  <Input
                    id={`${rowId}-pct`}
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={stage.percentage ?? ""}
                    onChange={(e) => updateStage(index, { percentage: e.target.value })}
                    placeholder="1-100"
                    className="mt-1"
                    data-testid={`${rowId}-pct`}
                  />
                </div>
              )}

              {postUat && !isFinal && (
                <div>
                  <Label className="text-xs text-white/80" htmlFor={`${rowId}-threshold`}>
                    Success threshold to move on (%)
                  </Label>
                  <Input
                    id={`${rowId}-threshold`}
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={stage.threshold ?? ""}
                    onChange={(e) => updateStage(index, { threshold: e.target.value })}
                    placeholder={thresholdPlaceholder(index)}
                    className="mt-1"
                    data-testid={`${rowId}-threshold`}
                  />
                  <p className="mt-1 text-xs text-white/60">Leave blank to use the tenant default.</p>
                </div>
              )}
              {postUat && isFinal && (
                <div className="flex items-end">
                  <p className="text-xs text-white/60">Final stage: no threshold needed.</p>
                </div>
              )}
            </div>

            {stage.type === STAGE_GROUP && (
              <p className="text-xs text-white/60">
                Stays assigned while later stages follow.
              </p>
            )}

            {errors.length > 0 && (
              <ul className="space-y-0.5" data-testid={`${rowId}-errors`}>
                {errors.map((message, i) => (
                  <li key={i} className="flex items-start gap-1 text-xs text-red-300">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    {message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {validation.general.length > 0 && (
        <ul className="space-y-0.5" data-testid={`${testIdPrefix}-errors`}>
          {validation.general.map((message, i) => (
            <li key={i} className="flex items-start gap-1 text-xs text-red-300">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
              {message}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!hasPreUat && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addPreUat}
            data-testid={`${testIdPrefix}-add-pre-uat`}
          >
            <Plus className="h-4 w-4 mr-1" />
            Pre-UAT stage
          </Button>
        )}
        {!atMax && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addGroup}
              data-testid={`${testIdPrefix}-add-group`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Named group stage
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addPercentage}
              data-testid={`${testIdPrefix}-add-percentage`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Percentage stage
            </Button>
          </>
        )}
        {atMax && (
          <span className="text-xs text-white/60">
            A pipeline can have at most {MAX_STAGES_AFTER_UAT} stages after UAT.
          </span>
        )}
      </div>
    </div>
  );
}
